import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {saveCallbackStatus} from '@finpay-development/shared-components';
import {
  convertedPatientFilter,
  emptyConvertedPatientFilter,
  emptyPreConvertedPatientFilter,
  PatientClientFilter,
  preConvertedPatientFilter,
} from '../components/models/patient-filter';
import {
  addPatientPaymentMethod,
  createS3File,
  deletePatientNote,
  deletePatientPaymentMethod,
  deleteS3Document,
  dismissReminder,
  getConvertedPatients,
  getEncounterSignUrl,
  getInstanceOfCare,
  getPaymentMethods,
  getS3Document,
  getTransactions,
  putProgram,
  releaseCharge,
  savePatientPaymentProgram,
  setNextPaymentDueDt,
  setUpEncountersAccountHolder,
  updateContactInfo,
  updateEncountersAfterMakePayment,
  verifyBankAccount,
} from './patient-thunk';
import {
  emptyLoadingState,
  loadingState,
} from '../components/models/loading-state';
import {
  PatientEncounterCard,
} from '../components/models/patient-encounter-card';
import {
  emptyPatientCurrentInsurance,
  emptyPatientViewModel, emptyPatientWaiver,
  PatientCurrentInsurance,
  PatientViewModel, PatientWaiver,
} from '../components/models/patient-view-model';
import {
  emptyPatientEncounter,
  PatientEncounter,
  patientEncounterSignUrl,
} from '../components/models/patient-encounter';
import {Rule} from '../../admin-configuration/models/rule';
import {PatientNotes} from '../components/models/patient-note';
import {CreateTokenCardData} from '@stripe/stripe-js';
import {TokenState} from '../../stripe/models/stripe-token-state';
import {
  emptyStripeBankData,
  StripeBankData,
} from '../../stripe/models/stripe-bank-data';
import {emptyErrorState, ErrorState} from '../components/models/error-state';
import {recentlyViewedPatientsObj} from '../../shared/model/recentlyViewed';
import {PrimaryContact} from '../../shared/model/primary-contact';
import {TimingRisk} from 'src/shared/model/timing-and-payor-risk';
import {Workflow} from '../../shared/model/client';
import {patientHelper} from '../services/patient-service';
import {blankState, emptyBlankState} from '../components/models/blank-state';
import {
  emptyReleasePaymentErrorContext,
  ReleasePaymentErrorContext,
} from '../components/models/release-payment-model';
import {PatientS3document} from '../components/models/patient-s3document';
import {emptyRiskClass, RiskClass} from '../../shared/model/risk-class';
import {
  AccountHolderContext,
  emptyAccountHolderContext,
} from '../components/models/account-holder-model';
import {DetailedLedger, Ledger} from '../../shared/model/ledger';
import {LedgerUtils} from '../../shared/ledger-utils';
import {ClientWaiver} from '../components/models/waiver';

export interface PatientState {
  preconvertedPatients: PatientEncounterCard[]; // all preconverted patients
  preconvertedPatientsFiltered: {[key: string]: PatientEncounterCard[]};
  convertedPatients: PatientEncounterCard[]; // all converted patients
  convertedPatientsFiltered: {[key: string]: PatientEncounterCard[]};
  selectedPatient: PatientViewModel; // patient object containing (possibly) multiple encounters
  selectedPatientCurrentInsurance: PatientCurrentInsurance
  selectedPatientWaiver: PatientWaiver
  selectedEncounter: PatientEncounter; // selected encounter via dropdown
  patientIocStatus: saveCallbackStatus,
  calculatedRiskClass: RiskClass;

  preConvertedFilterFormValues: preConvertedPatientFilter; // preconverted filter values
  convertedFilterFormValues: convertedPatientFilter; // converted filter values
  preConvertedClientAndFacilityData: PatientClientFilter[];
  convertedClientAndFacilityData: PatientClientFilter[];

  preConvertedClientCheckboxesChecked: Number[];
  convertedClientCheckboxesChecked: Number[];
  preConvertedFacilityCheckboxesChecked: Number[];
  convertedFacilityCheckboxesChecked: Number[];

  savePatientModalSaveStatus: saveCallbackStatus,
  downPaymentModalSaveStatus: saveCallbackStatus,
  instanceOfCareModalSaveStatus: saveCallbackStatus,
  completePatientModalIOCSaveStatus: saveCallbackStatus,
  downPaymentTokens: TokenState, // stripe token for down payment modal,
  recurringPaymentTokens: TokenState,
  patientTakePaymentTokens: TokenState,
  recurringPatientTakePaymentTokens: TokenState,
  patientMakePaymentTokens: TokenState,
  isPreConverted: boolean,
  recentlyViewedPreConvertedPatients: Array<recentlyViewedPatientsObj>, // preconverted recently viewed patients from cookies
  recentlyViewedConvertedPatients: Array<recentlyViewedPatientsObj> ,// converted recently viewed patients from cookies
  redirectToId: number,
  matchingRule: Rule,
  comments: PatientNotes[],
  reminders: PatientNotes[],
  stripeCardData: CreateTokenCardData,
  stripeBankData: StripeBankData,
  isError: ErrorState,
  isSaving: boolean,
  // common state for most/all slices
  errorMessage: string,
  isLoading: loadingState, // object containing loading state for multiple pages
  isConverting: boolean, // is in the process of converting an IOC
  isBlank: blankState, // object telling us whether a state field is actually empty (after api was called), or if was just initialized to empty. Not needed for all fields.
  convertedActiveEncounters: PatientEncounter[],
  convertedCompletedEncounters: PatientEncounter[],
  convertedCancelledEncounters: PatientEncounter[],
  selectedConvertedEncounters: PatientEncounter[], // list of encounters that are selected via Account Holder View. (one of active, converted, or completed encounters lists)
  currentConvertedEncounter: PatientEncounter, // focused encounter that is currently being interacted with by the user
  isDownPaymentFullyPaid: boolean,
  patientEncounterSignUrl: patientEncounterSignUrl, // patientEncounterUrl to sign
  transactionsCSVData: [string[]],
  refreshAccountHolderDashboard: boolean,
  patientS3Documents: Array<PatientS3document>,
  isUploadingS3: boolean,
  scriptWindowObjectReference: Array<any>
  clientsPatientIOCIdFilter?: Array<number>;
  inboundPtRecord?: PatientRecordRes;
  inboundPtRecordV2?: PatientRecordResV2;
  clientsPatientAccountIdFilter?: Array<number>;
  accountHolderContext: AccountHolderContext;
  releasePaymentStatusMsg: ReleasePaymentErrorContext;
  clientWaiversMap: Map<number, ClientWaiver>;
  recentPatients: RecentPatientItem[];
}

export interface PatientRecordRes {
  admissionDt: string;
  clientId: number;
  clientsPatientAccountId: string;
  clientsPatientIOCId: string;
  facilityId: number;
  patient: {
      contact: PrimaryContact;
      clientId: number;
      patientStatus: string;
  };
  patientId: number | null;
  patientInsurance: any;
  patientNotes: PatientNotes;
  timingRisk: TimingRisk;
  workflow: Workflow;
}

export interface PatientRecordResV2 {
  url: string;
}

export interface RecentPatientItem {
  patientId: number;
  patientName: string;
  birthDate: string;
}

const initialState = {
    preconvertedPatients: [],
    preconvertedPatientsFiltered: {},
    convertedPatients: [],
    convertedPatientsFiltered: {},
    selectedPatient: emptyPatientViewModel,
    selectedPatientCurrentInsurance:emptyPatientCurrentInsurance,
    selectedPatientWaiver: emptyPatientWaiver,
    selectedEncounter: emptyPatientEncounter,
    patientIocStatus: saveCallbackStatus.none,
    calculatedRiskClass: emptyRiskClass,
    preConvertedFilterFormValues: emptyPreConvertedPatientFilter,
    convertedFilterFormValues: emptyConvertedPatientFilter,
    preConvertedClientAndFacilityData: [],
    convertedClientAndFacilityData: [],

    preConvertedClientCheckboxesChecked: [],
    convertedClientCheckboxesChecked: [],
    preConvertedFacilityCheckboxesChecked: [],
    convertedFacilityCheckboxesChecked: [],

  savePatientModalSaveStatus: saveCallbackStatus.none,
  downPaymentModalSaveStatus: saveCallbackStatus.none,
  instanceOfCareModalSaveStatus: saveCallbackStatus.none,
  completePatientModalIOCSaveStatus: saveCallbackStatus.none,
  downPaymentTokens: {},
  recurringPaymentTokens: {},
  patientTakePaymentTokens: {},
  recurringPatientTakePaymentTokens: {},
  patientMakePaymentTokens: {},
  isPreConverted: true,
  recentlyViewedPreConvertedPatients: [],
  recentlyViewedConvertedPatients: [],
  redirectToId: 0,
  matchingRule: { ruleId: -1 } as Rule,
  comments: [],
  reminders: [],
  stripeCardData: {},
  stripeBankData: emptyStripeBankData,
  isError: emptyErrorState,
  isSaving: false,
  errorMessage: "",
  isLoading: emptyLoadingState,
  isConverting: false,
  isBlank: emptyBlankState,
  convertedActiveEncounters: [],
  convertedCompletedEncounters: [],
  convertedCancelledEncounters: [],
  selectedConvertedEncounters: [],
  currentConvertedEncounter: {} as PatientEncounter,
  isDownPaymentFullyPaid: false,
  patientEncounterSignUrl:{} as patientEncounterSignUrl,
  transactionsCSVData: [[]],
  refreshAccountHolderDashboard: false,
  patientS3Documents: [],
  isUploadingS3: false,
  scriptWindowObjectReference: [],
  clientsPatientIOCIdFilter: undefined,
  clientsPatientAccountIdFilter: undefined,
  inboundPtRecord: {} as PatientRecordRes,
  inboundPtRecordV2: undefined,
  accountHolderContext: emptyAccountHolderContext,
  releasePaymentStatusMsg:emptyReleasePaymentErrorContext,
  clientWaiversMap: new Map(),
  recentPatients: [],
} as PatientState;

// Slice
const patientContextSlice = createSlice({
    name: 'patientContext',
    initialState,
    reducers: {
        clearStatus: state => {
            state.savePatientModalSaveStatus = saveCallbackStatus.none;
            state.downPaymentModalSaveStatus = saveCallbackStatus.none;
            state.instanceOfCareModalSaveStatus = saveCallbackStatus.none;
            state.errorMessage = '';
        },
        
        clearEncounter: (state) => {
            state.selectedEncounter = emptyPatientEncounter;
        },
        setPreconvertedFilterFormValues: (
            state,
            action: PayloadAction<Partial<preConvertedPatientFilter>>
        ) => {
            state.preConvertedFilterFormValues = {
                ...state.preConvertedFilterFormValues,
                ...action.payload,
            };
        },
        setConvertedFilterFormValues: (
            state,
            action: PayloadAction<Partial<convertedPatientFilter>>
        ) => {
            state.convertedFilterFormValues = {
                ...state.convertedFilterFormValues,
                ...action.payload,
            };
        },
        setDownPaymentToken(state, action: PayloadAction<TokenState>) {
            state.downPaymentTokens = action.payload;
        },
        setRecurringPaymentToken(state, action: PayloadAction<TokenState>) {
            state.recurringPaymentTokens = action.payload;
        },
        setPatientTakePaymentToken(state, action: PayloadAction<TokenState>) {
            state.patientTakePaymentTokens = action.payload;
        },
        setRecurringPatientTakePaymentToken(
            state,
            action: PayloadAction<TokenState>
        ) {
            state.recurringPatientTakePaymentTokens = action.payload;
        },
        setPatientMakePaymentToken(state, action: PayloadAction<TokenState>) {
            state.patientMakePaymentTokens = action.payload;
        },
        setTransactionsCSVData: (state, action) => {
            state.transactionsCSVData = action.payload;
        },
        setCalculatedRemainingBalance(state, action: PayloadAction<any>) {
            const isAccountHolder = action.payload.isAccountHolder;
            if (isAccountHolder) {
                state.currentConvertedEncounter.calculatedRemainingBalance =
                    action.payload.calculatedRemainingBalance;
            } else {
                state.selectedEncounter.calculatedRemainingBalance =
                    action.payload.calculatedRemainingBalance;
            }
        },
        setSelectedPatientV2(state, action) {
          state.selectedPatient = {...emptyPatientViewModel, ...action.payload};
        },
        setMatchingRule(state, action) {
            state.matchingRule = action.payload;
        },
        clearComments: state => {
            state.comments = [];
            state.reminders = [];
        },
        clearSelectedPatient: state => {
            state.selectedPatient = emptyPatientViewModel;
        },
        clearSignUrl: state => {
            state.patientEncounterSignUrl = {} as patientEncounterSignUrl;
            state.isError.hellosign = false;
            state.isLoading.hellosign = false;
        },
        setRedirectToId: (state, action) => {
            state.redirectToId = action.payload;
        },
        setSelectedEncounter: (state, action) => {
            state.selectedEncounter = action.payload;
        },
        setPatientPaymentMethods: (state, action) => {
            state.selectedEncounter.patientPaymentMethods = action.payload;
        },
        setStripeCardData: (state, action) => {
            state.stripeCardData = action.payload;
        },
        setStripeBankData: (state, action) => {
            state.stripeBankData = action.payload;
        },
        setCurrentConvertedEncounter: (state, action) => {
            state.currentConvertedEncounter = action.payload;
        },
        resetPatientContext() {
            return initialState;
        },
        resetDataAccountHolder(state) {
            state.selectedConvertedEncounters = [];
            state.convertedActiveEncounters = [];
            state.convertedCompletedEncounters = [];
            state.convertedCancelledEncounters = [];
            state.selectedPatient = {} as PatientViewModel;
            state.selectedPatient.patientEncounters = [];
            state.currentConvertedEncounter = {} as PatientEncounter;
            state.isLoading.accountHolderDashboard = false;
            state.isError.accountHolderDashboard = false;
            state.refreshAccountHolderDashboard = true;
        },
        resetRefreshAccountHolderDashboard: state => {
            state.refreshAccountHolderDashboard = false;
        },
        addScriptWindowObjectReference(state, action: PayloadAction<any>) {
            state.scriptWindowObjectReference = [action.payload];
        },

        resetScriptWindowObjectReference(state) {
            state.scriptWindowObjectReference =
                initialState.scriptWindowObjectReference;
        },

      setLedgerData: (state, action: PayloadAction<{ ledger: Ledger[]; pagination: { hasMore?: boolean; count?: number } }>) => {
        // Initialize each ledger entry with detailsLoaded: false
        const ledgerWithLoadingFlags = action.payload.ledger.map(entry => ({
          ...entry,
          detailsLoaded: false
        }));
        
        // Calculate initial downpayments (will be recalculated when details are loaded)
        const downpaymentsData = LedgerUtils.calculateDownpaymentsData(ledgerWithLoadingFlags as DetailedLedger[]);
        
        state.selectedEncounter.encounterLedger = {
          ledger: ledgerWithLoadingFlags,
          downpayments: downpaymentsData.downpayments,
          downpaymentsSum: downpaymentsData.downpaymentsSum,
          pagination: action.payload.pagination
        };
      },
      updateLedgerWithDetails: (state, action: PayloadAction<DetailedLedger[]>) => {
        if (!state.selectedEncounter.encounterLedger || !state.selectedEncounter.encounterLedger.ledger) {
            console.warn("Cannot update ledger details - encounterLedger or ledger array is missing");
            return;
        }
        
        const detailedEntries = action.payload;
        if (!Array.isArray(detailedEntries) || detailedEntries.length === 0) {
            console.warn("No detailed ledger entries to update");
            return;
        }
        
        console.log(`Updating ${detailedEntries.length} ledger entries with details in Redux store`);
        
        // Get the current ledger entries
        const currentLedger = state.selectedEncounter.encounterLedger.ledger;
        
        // Update the ledger with detailed entries
        // For each detailed entry, find its corresponding ledger entry and update it
        detailedEntries.forEach(detailedEntry => {
            const index = currentLedger.findIndex(entry => 
                entry.patientLedgerId === detailedEntry.patientLedgerId);
            
            if (index !== -1) {
                // Update existing entry with details and mark as loaded
                currentLedger[index] = {
                    ...currentLedger[index],
                    ...detailedEntry,
                    detailsLoaded: true
                };
            } else {
                // Entry not found, don't add it to the ledger
                console.warn(`Ledger entry with ID ${detailedEntry.patientLedgerId} not found in current ledger. Skipping.`);
            }
        });
        
        // Sort the ledger by ledgerDt in descending order (newest first)
        currentLedger.sort((a, b) => {
            const dateA = new Date(a.ledgerDt).getTime();
            const dateB = new Date(b.ledgerDt).getTime();
            return dateB - dateA;
        });
        
        // Recalculate downpayments after updating ledger details
        const downpaymentsData = LedgerUtils.calculateDownpaymentsData(currentLedger as DetailedLedger[]);
        state.selectedEncounter.encounterLedger.downpayments = downpaymentsData.downpayments;
        state.selectedEncounter.encounterLedger.downpaymentsSum = downpaymentsData.downpaymentsSum;
        
        console.log(`Redux store updated with detailed ledger entries. Total entries: ${currentLedger.length}. Downpayments recalculated: ${downpaymentsData.downpayments.length} entries, sum: ${downpaymentsData.downpaymentsSum}`);
    },
    updateBalances(state, action: PayloadAction<{pendingBalance: number, capturedBalance: number}>) {
        if (state.selectedEncounter?.patientPaymentProgram) {
            const latestProgram = state.selectedEncounter.patientPaymentProgram[state.selectedEncounter.patientPaymentProgram.length - 1];
            if (latestProgram?.patientPaymentSchedule) {
                latestProgram.patientPaymentSchedule.pfrPendingBalance = action.payload.pendingBalance;
                latestProgram.patientPaymentSchedule.pfrCapturedBalance = action.payload.capturedBalance;
            }
        }
    },
    setPatientCurrentInsurance(state, action){
        state.selectedPatientCurrentInsurance = action.payload
    },
    setPatientWaiver(state, action){
      state.selectedPatientWaiver = action.payload
    }
    },
    extraReducers: builder => {
        // preConverted patients
        // converted patients
        builder.addCase(getConvertedPatients.pending, (state, action) => {
            state.isLoading.convertedPatients = true;
            state.isError.convertedPatients = false;
        });
        builder.addCase(getConvertedPatients.fulfilled, (state, action) => {
            state.isLoading.convertedPatients = false;
            const convertedPatients = action.payload.sort(
                (first: PatientEncounterCard, second: PatientEncounterCard) =>
                    //sort client name
                    first.clientName > second.clientName
                        ? 1
                        : // then by first and last name
                        first.clientName === second.clientName
                        ? first.contactCard.firstName +
                              ' ' +
                              first.contactCard.lastName >
                          second.contactCard.firstName +
                              ' ' +
                              second.contactCard.lastName
                            ? 1
                            : -1
                        : -1
            );
            state.convertedPatients = convertedPatients;
            state.convertedPatientsFiltered =
                patientHelper.filterConvertedPatients(convertedPatients);
            state.isError.convertedPatients = false;
        });
        builder.addCase(getConvertedPatients.rejected, (state, action) => {
            state.isLoading.convertedPatients = false;
            state.isError.convertedPatients = true;
        })

        builder.addCase(getPaymentMethods.pending, (state, action) => {
            state.isError.paymentMethods = false;
            state.isLoading.paymentMethods = true;
        });
        builder.addCase(getPaymentMethods.fulfilled, (state, action) => {
            const isAccountHolder = action.payload?.isAccountHolder; // if we are fetching payment methods in the account holder view (not patient records)
            if (isAccountHolder) {
                state.currentConvertedEncounter =
                    action.payload.selectedEncounter;
                state.selectedConvertedEncounters =
                    action.payload.patientEncounters;
            } else {
                state.selectedEncounter = action.payload.selectedEncounter;
                state.selectedPatient.patientEncounters =
                    action.payload.patientEncounters;
            }
            state.isError.paymentMethods = false;
            state.isLoading.paymentMethods = false;
        });
        builder.addCase(getPaymentMethods.rejected, (state, action) => {
            state.isError.paymentMethods = true;
            state.isLoading.paymentMethods = false;
        });

        builder.addCase(addPatientPaymentMethod.pending, (state, action) => {});
        builder.addCase(addPatientPaymentMethod.fulfilled, (state, action) => {
            const isAccountHolder = action.payload?.isAccountHolder;
            if (isAccountHolder) {
                state.currentConvertedEncounter =
                    action.payload.newSelectedEncounter;
                state.selectedConvertedEncounters =
                    action.payload.newEncounters;
            } else {
                state.selectedEncounter = action.payload.newSelectedEncounter;
                state.selectedPatient.patientEncounters =
                    action.payload.newEncounters;
            }
        });
        builder.addCase(
            addPatientPaymentMethod.rejected,
            (state, action) => {}
        );

        builder.addCase(
            deletePatientPaymentMethod.pending,
            (state, action) => {}
        );
        builder.addCase(
            deletePatientPaymentMethod.fulfilled,
            (state, action) => {
                const isAccountHolder = action.payload?.isAccountHolder;
                if (isAccountHolder) {
                    state.currentConvertedEncounter =
                        action.payload.newSelectedEncounter;
                    state.selectedConvertedEncounters =
                        action.payload.newEncounters;
                } else {
                    state.selectedEncounter =
                        action.payload.newSelectedEncounter;
                    state.selectedPatient.patientEncounters =
                        action.payload.newEncounters;
                }
            }
        );
        builder.addCase(
            deletePatientPaymentMethod.rejected,
            (state, action) => {}
        );
        // patient transactions
        builder.addCase(getTransactions.pending, (state, action) => {
            state.isError.patientTransactions = false;
            state.isLoading.patientTransactions = true;
        });
        builder.addCase(getTransactions.fulfilled, (state, action) => {
            const isAccountHolder = action.payload?.isAccountHolder; // if we are fetching transactions in the account holder view (not patient records)
            if (isAccountHolder) {
                state.currentConvertedEncounter =
                    action.payload.selectedEncounter;
                state.selectedConvertedEncounters =
                    action.payload.patientEncounters;
            } else {
                state.selectedEncounter = action.payload.selectedEncounter;
                state.selectedPatient.patientEncounters =
                    action.payload.patientEncounters;
            }
            state.isError.patientTransactions = false;
            state.isLoading.patientTransactions = false;
        });
        builder.addCase(getTransactions.rejected, (state, action) => {
            state.isLoading.patientTransactions = false;
            state.isError.patientTransactions = true;
        });

        // patient payment program
        builder.addCase(savePatientPaymentProgram.pending, (state, action) => {
            state.isLoading.paymentProgram = true;
        });
        builder.addCase(
            savePatientPaymentProgram.fulfilled,
            (state, action) => {
                state.isLoading.paymentProgram = false;
                state.selectedEncounter =
                    action.payload.selectedPatientEncounter;
                state.selectedPatient.patientEncounters =
                    action.payload.allPatientEncounters;
                state.isError.paymentProgramNeedsUpdate = false;
            }
        );
        builder.addCase(savePatientPaymentProgram.rejected, (state, action) => {
            state.isLoading.paymentProgram = false;
        });

        // put payment program
        builder.addCase(putProgram.pending, (state, action) => {
            state.isLoading.paymentProgram = true;
        });
        builder.addCase(
            putProgram.fulfilled,
            (state, action) => {
                if (action.payload !== false) {
                    state.isLoading.paymentProgram = false;
                    state.selectedEncounter =
                        action.payload.selectedPatientEncounter;
                    state.selectedPatient.patientEncounters =
                        action.payload.allPatientEncounters;
                    state.isError.paymentProgramNeedsUpdate = false;
                }
            }
        );
        builder.addCase(putProgram.rejected, (state, action) => {
            state.isLoading.paymentProgram = false;
        });

        // Next Payment Due Date
        builder.addCase(setNextPaymentDueDt.pending, (state, action) => {
            state.isLoading.paymentProgram = true;
        });
        builder.addCase(setNextPaymentDueDt.fulfilled, (state, action) => {
            state.isLoading.paymentProgram = false;
            if (action.payload) {
                console.log('action.payload from setNextPaymentDueDt:', action.payload)
                if ('selectedPatientEncounter' in action.payload) {
                    state.selectedEncounter = action.payload.selectedPatientEncounter;
                    state.selectedPatient.patientEncounters = action.payload.allPatientEncounters;
                } else if ('currentConvertedEncounter' in action.payload) {
                    state.currentConvertedEncounter = action.payload.currentConvertedEncounter;
                    state.selectedConvertedEncounters = action.payload.selectedConvertedEncounters;
                }
            }
        });
        builder.addCase(setNextPaymentDueDt.rejected, (state, action) => {
            state.isLoading.paymentProgram = false;
            state.errorMessage = action.error.message || 'Failed to set next payment due date';
        });

        builder.addCase(getInstanceOfCare.pending, (state, action) => {
            state.isLoading.patientIOC = true;
            state.isError.patientEncounter = false;
        });
        builder.addCase(getInstanceOfCare.fulfilled, (state, action) => {
            state.isLoading.patientIOC = false;
            state.isError.patientEncounter = false;
            
            // Get the new instance of care from the payload
            state.selectedEncounter = action.payload.selectedPatientEncounter;
            
            state.selectedPatient.patientEncounters =
                action.payload.allPatientEncounters;
        });
        builder.addCase(getInstanceOfCare.rejected, (state, action) => {
            state.isLoading.patientIOC = false;
            state.isError.patientEncounter = true;
        });

        builder.addCase(deletePatientNote.pending, (state, action) => {
            state.downPaymentModalSaveStatus = saveCallbackStatus.none;
        });
        builder.addCase(deletePatientNote.fulfilled, (state, action) => {
            const patientNotesId = action.payload.patientNotesId;
            const isReminder = action.payload.isReminder;
            state.downPaymentModalSaveStatus = saveCallbackStatus.success;

            if (isReminder) {
                state.reminders = state.reminders.filter(function (reminders: any) {
                    return reminders.patientNotesId !== patientNotesId;
                });
            } else {
                state.comments = state.comments.filter(function (comments: any) {
                    return comments.patientNotesId !== patientNotesId;
                });
            }
        });
        builder.addCase(deletePatientNote.rejected, (state, action) => {
            state.downPaymentModalSaveStatus = saveCallbackStatus.error;
        });
        builder.addCase(dismissReminder.pending, (state, action) => {
            state.downPaymentModalSaveStatus = saveCallbackStatus.none;
        });
        builder.addCase(dismissReminder.fulfilled, (state, action) => {
            const patientNotesId = action.payload.patientNotesId;
            state.downPaymentModalSaveStatus = saveCallbackStatus.success;
            state.reminders = state.reminders.filter(function (reminders: any) {
                return reminders.patientNotesId !== patientNotesId;
            });
        });
        builder.addCase(dismissReminder.rejected, (state, action) => {
            state.downPaymentModalSaveStatus = saveCallbackStatus.error;
        });
        // 
        builder.addCase(updateEncountersAfterMakePayment.fulfilled, (state, action) => {
            const isAccountHolder = action.payload?.isAccountHolder; // if we are fetching transactions in the account holder view (not patient records)
            if (isAccountHolder) {
                state.selectedConvertedEncounters =
                    action.payload.newEncounters;
                state.currentConvertedEncounter =
                    action.payload.newSelectedEncounter;
            } else {
                state.selectedEncounter = action.payload.newSelectedEncounter;
                state.selectedPatient.patientEncounters =
                    action.payload.newEncounters;
            }
        });
        builder.addCase(releaseCharge.pending, (state, action) => {
            state.isError.releaseCharge = false;
        });
        builder.addCase(releaseCharge.fulfilled, (state, action) => {
            state.selectedEncounter = action.payload.newSelectedEncounter;
            state.selectedPatient.patientEncounters =
                action.payload.newEncounters;
            state.isError.releaseCharge = false;
        });
        builder.addCase(releaseCharge.rejected, (state, action) => {
            state.releasePaymentStatusMsg = {
                errorMsg: action.error.message!,
                paymentId: action.meta.arg.payment.paymentId!,
                encounterId: action.meta.arg.encounterId!,
                patientId: action.meta.arg.patientId!,
            };
            state.isError.releaseCharge = true;
        });
        builder.addCase(verifyBankAccount.pending, (state, action) => {});
        builder.addCase(verifyBankAccount.fulfilled, (state, action) => {
            // todo; need to see a successful response first
        });
        builder.addCase(verifyBankAccount.rejected, (state, action) => {});

        builder.addCase(getEncounterSignUrl.fulfilled, (state, action) => {
            state.isLoading.patientIOC = false;
            state.patientEncounterSignUrl = action.payload;
            state.isLoading.hellosign = false;
            state.isError.hellosign = false;
        });
        builder.addCase(getEncounterSignUrl.pending, (state, action) => {
            state.isError.hellosign = false;
            state.isLoading.hellosign = true;
        });
        builder.addCase(getEncounterSignUrl.rejected, (state, action) => {
            state.isError.hellosign = true;
            state.isLoading.hellosign = false;
        });

        // Account Holder Dashboard Initialization
        builder.addCase(
            setUpEncountersAccountHolder.pending,
            (state, action) => {
                state.isLoading.accountHolderDashboard = true;
                state.isError.accountHolderDashboard = false;
            }
        );
        builder.addCase(
            setUpEncountersAccountHolder.fulfilled,
            (state, action) => {
                state.selectedConvertedEncounters =
                    action.payload.currentSelectedEncounters;
                state.convertedActiveEncounters =
                    action.payload.activeEncounters;
                state.convertedCompletedEncounters =
                    action.payload.completedEncounters;
                state.convertedCancelledEncounters =
                    action.payload.cancelledEncounters;
                state.selectedPatient = action.payload.selectedPatient;
                state.selectedPatient.patientEncounters =
                    action.payload.encountersList;

                state.isLoading.accountHolderDashboard = false;
                state.isError.accountHolderDashboard = false;
            }
        );
        builder.addCase(
            setUpEncountersAccountHolder.rejected,
            (state, action) => {
                state.isLoading.accountHolderDashboard = false;
                state.isError.accountHolderDashboard = true;
            }
        );
        builder.addCase(updateContactInfo.fulfilled, (state, action) => {
            state.selectedConvertedEncounters =
                action.payload.selectedConvertedEncounters;
            state.isSaving = false;
        });
        builder.addCase(updateContactInfo.pending, (state, action) => {
            state.isSaving = true;
        });
        builder.addCase(createS3File.pending, (state, action) => {
            state.isUploadingS3 = true;
        });
        builder.addCase(createS3File.fulfilled, (state, action) => {
            state.isUploadingS3 = false;
        });
        builder.addCase(createS3File.rejected, (state, action) => {
            state.isUploadingS3 = false;
        });
        builder.addCase(getS3Document.pending, (state, action) => {
            state.isUploadingS3 = true;
        });
        builder.addCase(getS3Document.fulfilled, (state, action) => {
            state.patientS3Documents = action.payload;
            state.isUploadingS3 = false;
        });
        builder.addCase(getS3Document.rejected, (state, action) => {
            state.isUploadingS3 = false;
        });
        builder.addCase(deleteS3Document.pending, (state, action) => {
            state.isUploadingS3 = true;
        });
        builder.addCase(deleteS3Document.fulfilled, (state, action) => {
            state.isUploadingS3 = false;
        });
    },
});

export const {
    setPreconvertedFilterFormValues,
    setDownPaymentToken,
    setRecurringPaymentToken,
    setPatientTakePaymentToken,
    setRecurringPatientTakePaymentToken,
    setPatientMakePaymentToken,
    setConvertedFilterFormValues,
    setSelectedEncounter,
    setPatientPaymentMethods,
    clearEncounter,
    setTransactionsCSVData,
    clearSelectedPatient,
    resetDataAccountHolder,
    resetRefreshAccountHolderDashboard,
    setStripeCardData,
    setStripeBankData,
    setCurrentConvertedEncounter,
    setCalculatedRemainingBalance,
    resetPatientContext,
    setSelectedPatientV2,
    setMatchingRule,
    clearSignUrl,
    setLedgerData,
    updateLedgerWithDetails,
    updateBalances,
    setPatientCurrentInsurance,
    setPatientWaiver
} = patientContextSlice.actions;
export default patientContextSlice.reducer;
